import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Calendar, Map, Inbox, FileCheck, CreditCard, BarChart3, Truck, Users, UserCircle, LogOut, Menu, X, Bell, History } from 'lucide-react';
import { useSupplierAuth } from '../lib/supplierAuthStore';
import { useSupplierStore } from '../lib/supplierStore';
import { PoweredByRTA } from './PoweredByRTA';
const NAV_ITEMS = [{
  id: 'dashboard',
  label: 'Overview',
  icon: LayoutDashboard,
  path: '/supplier/dashboard'
}, {
  id: 'dispatcher',
  label: 'Dispatcher',
  icon: Calendar,
  path: '/supplier/dispatcher'
}, {
  id: 'tracking',
  label: 'Live Tracking',
  icon: Map,
  path: '/supplier/tracking'
}, {
  id: 'requests',
  label: 'Requests & Bids',
  icon: Inbox,
  path: '/supplier/requests',
  badge: '3'
}, {
  id: 'history',
  label: 'Job History',
  icon: History,
  path: '/supplier/history'
}, {
  id: 'permits',
  label: 'Permits',
  icon: FileCheck,
  path: '/supplier/permits'
}, {
  id: 'payments',
  label: 'Payments',
  icon: CreditCard,
  path: '/supplier/payments'
}, {
  id: 'reports',
  label: 'Reports',
  icon: BarChart3,
  path: '/supplier/reports'
}, {
  id: 'trucks',
  label: 'Fleet',
  icon: Truck,
  path: '/supplier/trucks'
}, {
  id: 'drivers',
  label: 'Drivers',
  icon: Users,
  path: '/supplier/drivers'
}];
export function SupplierLayout({
  children
}: {
  children: React.ReactNode;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    user,
    isSignedIn,
    signOut
  } = useSupplierAuth();
  const {
    autoDispatch
  } = useSupplierStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Auth guard — redirect unauthenticated users to login.
  useEffect(() => {
    if (!isSignedIn) {
      navigate('/supplier/login', {
        replace: true
      });
    }
  }, [isSignedIn, navigate]);
  if (!isSignedIn) return null;
  const handleSignOut = () => {
    signOut();
    navigate('/supplier/login');
  };
  return <div className="min-h-screen bg-surface-100 flex flex-col lg:flex-row" data-id="element-660">
      {/* Mobile Header */}
      <div className="lg:hidden bg-gray-900 text-white p-4 flex items-center justify-between sticky top-0 z-50" data-id="element-661">
        <Link to="/" aria-label="Pair2 — switch portal" title="Switch portal" className="flex items-center gap-2 group" data-id="element-662">
          <img src="https://cdn.magicpatterns.com/uploads/kwTMidwDpg46YyVPyEcdAS/Pair2_(2).svg" alt="Pair2" className="h-7 w-auto brightness-0 invert group-hover:opacity-90 transition-opacity" data-id="element-663" />
          <span className="text-[10px] font-bold uppercase tracking-wider text-brand-500 border border-brand-500/40 px-1.5 py-0.5 rounded" data-id="element-664">
            Carrier
          </span>
        </Link>
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2" data-id="element-665">
          {mobileMenuOpen ? <X className="w-6 h-6" data-id="element-666" /> : <Menu className="w-6 h-6" data-id="element-667" />}
        </button>
      </div>

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-40 w-64 bg-gray-900 text-gray-300 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:shrink-0 flex flex-col ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`} data-id="element-668">
        <Link to="/" aria-label="Pair2 — switch portal" title="Switch portal" className="p-6 hidden lg:flex items-center gap-2 group" onClick={() => setMobileMenuOpen(false)} data-id="element-669">
          <img src="https://cdn.magicpatterns.com/uploads/kwTMidwDpg46YyVPyEcdAS/Pair2_(2).svg" alt="Pair2" className="h-8 w-auto brightness-0 invert group-hover:opacity-90 transition-opacity" data-id="element-670" />
          <span className="text-[10px] font-bold uppercase tracking-wider text-brand-500 border border-brand-500/40 px-1.5 py-0.5 rounded" data-id="element-671">
            Carrier
          </span>
        </Link>

        <div className="flex-1 overflow-y-auto py-4 px-3 space-y-1" data-id="element-672">
          {NAV_ITEMS.map(item => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;
          return <Link key={item.id} to={item.path} onClick={() => setMobileMenuOpen(false)} className={`flex items-center justify-between px-4 py-3 rounded-xl transition-colors ${isActive ? 'bg-brand-500/10 text-brand-400 font-semibold' : 'hover:bg-gray-800 hover:text-white'}`} data-id="element-673">
                <div className="flex items-center gap-3" data-id="element-674">
                  <Icon className={`w-5 h-5 ${isActive ? 'text-brand-400' : 'text-gray-400'}`} data-id="element-675" />
                  {item.label}
                </div>
                <div className="flex items-center gap-1.5" data-id="element-676">
                  {item.id === 'dispatcher' && autoDispatch?.enabled && <span className="flex items-center gap-1 bg-emerald-500/15 text-emerald-400 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border border-emerald-500/30" data-id="element-677">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" data-id="element-678" />
                      Auto
                    </span>}
                  {item.badge && <span className="bg-brand-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full" data-id="element-679">
                      {item.badge}
                    </span>}
                </div>
              </Link>;
        })}
        </div>

        <div className="p-4 border-t border-gray-800" data-id="element-680">
          <Link to="/supplier/profile" onClick={() => setMobileMenuOpen(false)} className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors mb-2 ${location.pathname === '/supplier/profile' ? 'bg-gray-800 text-white font-semibold' : 'hover:bg-gray-800 hover:text-white'}`} data-id="element-681">
            <UserCircle className="w-5 h-5 text-gray-400" data-id="element-682" />
            Profile & Settings
          </Link>
          <button onClick={handleSignOut} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-800 hover:text-white transition-colors text-left" data-id="element-683">
            <LogOut className="w-5 h-5 text-gray-400" data-id="element-684" />
            Sign out
          </button>
          <div className="mt-4 pt-4 border-t border-gray-800/60 flex justify-center" data-id="element-685">
            <PoweredByRTA variant="dark" data-id="element-686" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden" data-id="element-687">
        {/* Topbar (Desktop) */}
        <div className="hidden lg:flex h-16 bg-white border-b border-gray-200 items-center justify-between px-8 shrink-0" data-id="element-688">
          <h1 className="font-bold text-gray-900 text-lg" data-id="element-689">
            {NAV_ITEMS.find(i => i.path === location.pathname)?.label || 'Profile'}
          </h1>
          <div className="flex items-center gap-6" data-id="element-690">
            <button className="relative p-2 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-691">
              <Bell className="w-5 h-5" data-id="element-692" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-brand-500 rounded-full border-2 border-white" data-id="element-693"></span>
            </button>
            <div className="flex items-center gap-3 pl-6 border-l border-gray-200" data-id="element-694">
              <div className="text-right" data-id="element-695">
                <p className="text-sm font-bold text-gray-900" data-id="element-696">
                  {user?.businessName || 'FastTrack Logistics'}
                </p>
                <p className="text-[10px] uppercase tracking-wider text-gray-500" data-id="element-697">
                  {user?.phone}
                </p>
              </div>
              <div className="w-10 h-10 rounded-full bg-gray-900 flex items-center justify-center text-white font-bold" data-id="element-698">
                {(user?.businessName || 'F')[0].toUpperCase()}
              </div>
            </div>
          </div>
        </div>

        {/* Page Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8" data-id="element-699">
          {children}
        </div>
      </div>

      {/* Mobile Overlay */}
      {mobileMenuOpen && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setMobileMenuOpen(false)} data-id="element-700" />}
    </div>;
}